
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import TakeOrderListItem from '@/modules/DispatcherModule/components/TakeOrderListItem/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// types
import { DispatcherTakesListOrderT, DispatcherTakeStatusT } from '@/core/types/dispatcher.types'
import { DispatcherCourierT } from '@/core/types/couriers.types'
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'
import { CompanyT } from '@/core/types/companies.types'
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        list: List,
        'the-select': TheSelect,
        'the-input': TheInput,
        'the-button': TheButton,
        //
        'take-order-list-item': TakeOrderListItem,
    },
    setup() {
        const account = useAccount()
        const user_id = account.state.user_id

        const state = reactive(new DataState<DispatcherTakesListOrderT>({}))

        const filters_state = reactive({
            dispatchers_sector_id: null as number | null,
            courier_id: null as number | null,
            invoice_number: null,
            order_id: null as number | null,
            order_number_1c: null as string | null,
            status_ids: [1, 2] as number[],
            company_ids: [] as number[],
        })

        const loadTakesList = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const response = await dispatcher.getTakesList({
                    page,
                    dispatcher_sector_id: filters_state.dispatchers_sector_id,
                    courier_id: filters_state.courier_id,
                    invoice_number: filters_state.invoice_number,
                    order_id: filters_state.order_id,
                    order_number_1c: filters_state.order_number_1c,
                    status_ids: filters_state.status_ids,
                    company_ids: filters_state.company_ids,
                })

                state.list = response.data
                state.pagination.total_pages = response.meta.last_page

                state.is_failed = false
                state.is_completed = true
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const data_state = reactive({
            couriers: new DataState<DispatcherCourierT>({}),
            dispatchers_sectors: new DataState<DispatcherSectorT>({}),
            statuses: new DataState<DispatcherTakeStatusT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({ page, search, statusId: 1 })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 1 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const loadCouriers = async (page: number, search?: string) => {
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const response = await couriers.getList({
                    page,
                    name: search,
                    statusIds: [2],
                })

                data_state.couriers.list = response.data
                data_state.couriers.pagination.total_pages = response.meta.last_page

                data_state.couriers.is_failed = false
                data_state.couriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.couriers.failure.catchError(error)

                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_failed = true
                }
            } finally {
                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_loading = false
                }
            }
        }

        const loadMoreCouriers = async (page: number) => {
            if (page > data_state.couriers.pagination.total_pages) return
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const response = await couriers.getList({ page, statusIds: [2] })
                data_state.couriers.list = data_state.couriers.list.concat(response.data)

                data_state.couriers.pagination.total_pages = response.meta.last_page

                data_state.couriers.is_failed = false
                data_state.couriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.couriers.failure.catchError(error)

                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_failed = true
                }
            } finally {
                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_loading = false
                }
            }
        }

        const searchCourier = async (search_query: string) => {
            // data_state.countries.pagination.current_page
            loadCouriers(1, search_query)
        }

        const unshiftFindedCourier = async (courier: DispatcherCourierT) => {
            await loadCouriers(data_state.couriers.pagination.current_page)
            data_state.couriers.list.unshift(courier)
        }

        const loadDispatchersSectors = async () => {
            try {
                data_state.dispatchers_sectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                data_state.dispatchers_sectors.list = response.data

                data_state.dispatchers_sectors.is_failed = false
                data_state.dispatchers_sectors.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatchers_sectors.failure.catchError(error)

                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_failed = true
                }
            } finally {
                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            state.is_loading = true

            loadCompanies(1)
            loadCouriers(1)
            loadStatuses()
            await loadDispatchersSectors()

            // Автозаполнение сектора
            if (data_state.dispatchers_sectors.list) {
                let auto_complete_sector = data_state.dispatchers_sectors.list.find(
                    (s) => s.users && s.users.find((user) => user.id === user_id)
                )
                if (auto_complete_sector) {
                    filters_state.dispatchers_sector_id = auto_complete_sector.id
                }
            }

            loadTakesList(1)
        })

        const onOrderRemoved = () => {
            loadTakesList(1)
        }

        const onFiltersReset = () => {
            filters_state.dispatchers_sector_id = null
            filters_state.courier_id = null
            filters_state.order_id = null
            filters_state.order_number_1c = null
            filters_state.status_ids = []
            filters_state.company_ids = []

            loadTakesList(state.pagination.current_page)
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true
                const response = await dispatcher.getStatuses()
                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        return {
            state,
            filters_state,
            data_state,
            loadTakesList,
            onOrderRemoved,
            loadCouriers,
            loadMoreCouriers,
            searchCourier,
            unshiftFindedCourier,
            loadDispatchersSectors,
            onFiltersReset,
            loadStatuses,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
        }
    },
})
